import { useEffect } from "react";

export default function useNewHomepageEffects(rootRef, html) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root || !html) return;

    const cleanups = [];

    // Navbar scroll
    const navbar = root.querySelector("#navbar");
    const onScroll = () => {
      if (navbar) navbar.classList.toggle("scrolled", window.scrollY > 40);
    };
    window.addEventListener("scroll", onScroll);
    onScroll();
    cleanups.push(() => window.removeEventListener("scroll", onScroll));

    // Mobile nav drawer (rendered outside .new-homepage-root to avoid overflow clipping)
    const navMenuToggle = root.querySelector("#navMenuToggle");
    const navMobileDrawer = document.getElementById("navMobileDrawer");
    const navMobileBackdrop = document.getElementById("navMobileBackdrop");
    const navMobileClose = document.getElementById("navMobileClose");
    const navMobileLinks = document.querySelectorAll(".nav-mobile-drawer .nav-mobile-link");
    const homepageRoot = root.closest(".new-homepage-root") || root;

    const setNavMenuOpen = (open) => {
      navMobileDrawer?.classList.toggle("is-open", open);
      navMenuToggle?.classList.toggle("is-open", open);
      navMenuToggle?.setAttribute("aria-expanded", open ? "true" : "false");
      navMenuToggle?.setAttribute("aria-label", open ? "Close menu" : "Open menu");
      navMobileDrawer?.setAttribute("aria-hidden", open ? "false" : "true");
      homepageRoot?.classList.toggle("nav-menu-open", open);
      document.body.classList.toggle("nav-menu-open", open);
    };

    const closeNavMenu = () => setNavMenuOpen(false);
    const openNavMenu = () => setNavMenuOpen(true);

    const onNavToggleClick = () => {
      const isOpen = navMobileDrawer?.classList.contains("is-open");
      setNavMenuOpen(!isOpen);
    };

    const onNavEscape = (event) => {
      if (event.key === "Escape") closeNavMenu();
    };

    navMenuToggle?.addEventListener("click", onNavToggleClick);
    navMobileBackdrop?.addEventListener("click", closeNavMenu);
    navMobileClose?.addEventListener("click", closeNavMenu);
    window.addEventListener("keydown", onNavEscape);
    navMobileLinks.forEach((link) => {
      link.addEventListener("click", closeNavMenu);
    });

    cleanups.push(() => {
      navMenuToggle?.removeEventListener("click", onNavToggleClick);
      navMobileBackdrop?.removeEventListener("click", closeNavMenu);
      navMobileClose?.removeEventListener("click", closeNavMenu);
      window.removeEventListener("keydown", onNavEscape);
      navMobileLinks.forEach((link) => {
        link.removeEventListener("click", closeNavMenu);
      });
      homepageRoot?.classList.remove("nav-menu-open");
      document.body.classList.remove("nav-menu-open");
    });

    // Relocate about pills → footer tagline; DPIIT card → team header (all viewports)
    const pillsRelocateTarget = root.querySelector('[data-relocate-target="pills"]');
    const featuredRelocateTarget = root.querySelector('[data-relocate-target="featured"]');
    const aboutStoryPills = root.querySelector(".about-story-pills");
    const aboutFeatured = root.querySelector(".about-featured");
    const footerTagline = root.querySelector(".footer-tagline-typewriter");
    const teamHeader = root.querySelector(".team-header");

    if (aboutStoryPills && pillsRelocateTarget && footerTagline) {
      footerTagline.after(aboutStoryPills);
      aboutStoryPills.classList.add("about-story-pills--relocated");
    }

    if (aboutFeatured && featuredRelocateTarget && teamHeader) {
      teamHeader.after(aboutFeatured);
      aboutFeatured.classList.add("about-featured--relocated");
    }

    cleanups.push(() => {
      if (aboutStoryPills && pillsRelocateTarget) {
        pillsRelocateTarget.after(aboutStoryPills);
      }
      if (aboutFeatured && featuredRelocateTarget) {
        featuredRelocateTarget.after(aboutFeatured);
      }
      aboutStoryPills?.classList.remove("about-story-pills--relocated");
      aboutFeatured?.classList.remove("about-featured--relocated");
    });

    // Announce bar carousel (2 slides)
    const announceSlides = root.querySelectorAll(".announce-slide");
    const announceIndicators = root.querySelectorAll(".announce-indicator");
    let announceIndex = 0;
    let announceTimer;

    if (announceSlides.length > 1) {
      const showAnnounceSlide = (index) => {
        announceSlides.forEach((slide, i) => {
          slide.classList.toggle("active", i === index);
        });
        announceIndicators.forEach((dot, i) => {
          dot.classList.toggle("active", i === index);
        });
        announceIndex = index;
      };

      const startAnnounceTimer = () => {
        clearInterval(announceTimer);
        announceTimer = setInterval(() => {
          showAnnounceSlide((announceIndex + 1) % announceSlides.length);
        }, 5000);
      };

      startAnnounceTimer();

      announceIndicators.forEach((dot) => {
        const onDotClick = () => {
          const index = Number(dot.dataset.index);
          if (!Number.isNaN(index)) {
            showAnnounceSlide(index);
            startAnnounceTimer();
          }
        };
        dot.addEventListener("click", onDotClick);
        cleanups.push(() => dot.removeEventListener("click", onDotClick));
      });

      cleanups.push(() => clearInterval(announceTimer));
    }

    // Scroll reveal
    const reveals = root.querySelectorAll(".reveal");
    const revealObs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("visible");
        });
      },
      { threshold: 0.1 }
    );
    reveals.forEach((el) => revealObs.observe(el));
    cleanups.push(() => revealObs.disconnect());

    // Section tags — swipe in from left once on first viewport entry
    const sectionTags = root.querySelectorAll(".section-tag-reveal");
    const sectionTagObs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            sectionTagObs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    sectionTags.forEach((el) => sectionTagObs.observe(el));
    cleanups.push(() => sectionTagObs.disconnect());

    // Footer — logo fade, then tagline typewriter (once)
    const footerBrand = root.querySelector(".footer-brand");
    let footerSequenceStarted = false;
    const footerTypewriterTimers = [];

    const startFooterTaglineTypewriter = (taglineEl) => {
      if (!taglineEl || taglineEl.dataset.typed === "true") return;
      taglineEl.dataset.typed = "true";

      const textEl = taglineEl.querySelector(".footer-tagline-text");
      const words = ["Connect.", "Learn.", "Grow."];
      let wordIdx = 0;
      let charIdx = 0;

      taglineEl.classList.add("is-typing");

      const typeStep = () => {
        if (!textEl) return;
        if (wordIdx >= words.length) {
          taglineEl.classList.remove("is-typing");
          taglineEl.classList.add("is-done");
          return;
        }
        const word = words[wordIdx];
        if (charIdx < word.length) {
          textEl.textContent += word[charIdx];
          charIdx += 1;
          footerTypewriterTimers.push(setTimeout(typeStep, 68));
        } else {
          if (wordIdx < words.length - 1) textEl.textContent += " ";
          wordIdx += 1;
          charIdx = 0;
          footerTypewriterTimers.push(setTimeout(typeStep, 420));
        }
      };

      typeStep();
    };

    if (footerBrand) {
      const footerObs = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting || footerSequenceStarted) return;
            footerSequenceStarted = true;
            footerObs.unobserve(footerBrand);

            const logo = footerBrand.querySelector(".footer-logo-reveal");
            const tagline = footerBrand.querySelector(".footer-tagline-typewriter");

            const beginTypewriter = () => startFooterTaglineTypewriter(tagline);

            if (logo) {
              logo.classList.add("visible");
              let typewriterQueued = false;
              const queueTypewriter = () => {
                if (typewriterQueued) return;
                typewriterQueued = true;
                beginTypewriter();
              };
              logo.addEventListener("transitionend", (e) => {
                if (e.propertyName === "opacity") queueTypewriter();
              });
              footerTypewriterTimers.push(setTimeout(queueTypewriter, 1750));
            } else {
              beginTypewriter();
            }
          });
        },
        { threshold: 0.15 }
      );
      footerObs.observe(footerBrand);
      cleanups.push(() => {
        footerObs.disconnect();
        footerTypewriterTimers.forEach((id) => clearTimeout(id));
      });
    }

    // About stats — directional card slide-in + count-up (trigger when stats panel is in view)
    let aboutStatsTriggered = false;
    const aboutStatsRoot = root.querySelector(".about-stats-panel");
    if (aboutStatsRoot) {
      const aboutStatCards = aboutStatsRoot.querySelectorAll(
        ".about-stat-featured, .about-stat-card"
      );
      const aboutStatTimers = [];
      const aboutStatRafIds = [];

      const formatIndianCount = (value) => {
        const n = Math.round(value);
        const digits = String(n);
        if (digits.length <= 3) return digits;
        const lastThree = digits.slice(-3);
        const rest = digits.slice(0, -3);
        return `${rest.replace(/\B(?=(\d{2})+(?!\d))/g, ",")},${lastThree}`;
      };

      const resetAboutStatCount = (el) => {
        const type = el.dataset.countType || "number";
        if (type === "ratio") {
          el.textContent = "1:0";
          return;
        }
        el.textContent = `0${el.dataset.countSuffix || ""}`;
      };

      const runAboutStatCounts = () => {
        const els = Array.from(aboutStatsRoot.querySelectorAll("[data-stat-count]"));
        const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        if (reducedMotion) {
          els.forEach((el) => {
            if (el.dataset.countFinal) {
              el.textContent = el.dataset.countFinal;
            }
          });
          return;
        }

        const duration = 2500;
        const startTime = performance.now();

        const updateEl = (el, progress) => {
          const type = el.dataset.countType || "number";
          const end = parseFloat(el.dataset.countEnd || "0");
          const suffix = el.dataset.countSuffix || "";
          const decimals = parseInt(el.dataset.countDecimals || "0", 10);
          const clamped = Math.min(Math.max(progress, 0), 1);

          if (type === "ratio") {
            el.textContent = `1:${Math.floor(end * clamped)}`;
            return;
          }
          if (type === "indian") {
            el.textContent = `${formatIndianCount(end * clamped)}${suffix}`;
            return;
          }
          if (decimals > 0) {
            el.textContent = `${(end * clamped).toFixed(decimals)}${suffix}`;
            return;
          }
          el.textContent = `${Math.floor(end * clamped)}${suffix}`;
        };

        const frame = (now) => {
          const t = Math.min((now - startTime) / duration, 1);
          const eased = 1 - (1 - t) ** 4;

          if (t < 1) {
            els.forEach((el) => updateEl(el, eased));
            aboutStatRafIds.push(requestAnimationFrame(frame));
            return;
          }

          els.forEach((el) => {
            if (el.dataset.countFinal) {
              el.textContent = el.dataset.countFinal;
            } else {
              updateEl(el, 1);
            }
          });
        };

        els.forEach((el) => updateEl(el, 0));
        aboutStatRafIds.push(requestAnimationFrame(frame));
      };

      const clearAboutStatCounts = () => {
        aboutStatRafIds.forEach((id) => cancelAnimationFrame(id));
        aboutStatRafIds.length = 0;
        aboutStatsRoot.querySelectorAll("[data-stat-count]").forEach(resetAboutStatCount);
      };

      const getAboutStatSlideOffset = (card) => {
        if (card.classList.contains("about-stat-slide-top")) return { x: 0, y: -52 };
        if (card.classList.contains("about-stat-slide-left")) return { x: -52, y: 0 };
        return { x: 52, y: 0 };
      };

      const animateAboutStatCard = (card) => {
        const { x, y } = getAboutStatSlideOffset(card);
        card.classList.add("about-stat-visible");

        const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (reducedMotion) {
          card.style.opacity = "";
          card.style.transform = "";
          return;
        }

        card.style.opacity = "0";
        card.style.transform = `translate3d(${x}px, ${y}px, 0)`;

        const animation = card.animate(
          [
            { opacity: 0, transform: `translate3d(${x}px, ${y}px, 0)` },
            { opacity: 1, transform: "translate3d(0, 0, 0)" },
          ],
          {
            duration: 820,
            easing: "cubic-bezier(0.22, 1, 0.36, 1)",
            fill: "forwards",
          }
        );

        animation.onfinish = () => {
          card.style.opacity = "";
          card.style.transform = "";
        };
      };

      const clearAboutStatReveal = () => {
        aboutStatTimers.forEach((id) => clearTimeout(id));
        aboutStatTimers.length = 0;
        clearAboutStatCounts();
        aboutStatCards.forEach((card) => {
          card.classList.remove("about-stat-visible");
          card.style.opacity = "";
          card.style.transform = "";
          card.getAnimations().forEach((anim) => anim.cancel());
        });
      };

      aboutStatCards.forEach((card) => {
        card.classList.remove("about-stat-visible");
        card.style.opacity = "0";
        const { x, y } = getAboutStatSlideOffset(card);
        card.style.transform = `translate3d(${x}px, ${y}px, 0)`;
      });
      aboutStatsRoot.querySelectorAll("[data-stat-count]").forEach(resetAboutStatCount);

      const revealAboutStatCards = () => {
        if (aboutStatsTriggered) return;
        aboutStatsTriggered = true;
        runAboutStatCounts();

        const staggerMs = 180;
        aboutStatCards.forEach((card, index) => {
          const timerId = window.setTimeout(() => {
            animateAboutStatCard(card);
          }, index * staggerMs);
          aboutStatTimers.push(timerId);
        });
      };

      const statsObs = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              revealAboutStatCards();
              statsObs.unobserve(aboutStatsRoot);
            }
          });
        },
        { threshold: 0.12, rootMargin: "0px 0px -4% 0px" }
      );

      requestAnimationFrame(() => {
        statsObs.observe(aboutStatsRoot);
      });
      cleanups.push(() => {
        statsObs.disconnect();
        clearAboutStatReveal();
        aboutStatsTriggered = false;
      });
    }

    // Problem cards sequential reveal
    const problemSection = root.querySelector("#problemCards");
    let cardsTriggered = false;
    if (problemSection) {
      const problemCards = problemSection.querySelectorAll(".problem-card");
      const problemObs = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !cardsTriggered) {
              cardsTriggered = true;
              problemCards.forEach((card, i) => {
                setTimeout(() => card.classList.add("card-visible"), i * 180);
              });
            }
          });
        },
        { threshold: 0.2 }
      );
      problemObs.observe(problemSection);
      cleanups.push(() => problemObs.disconnect());

      const problemCardLb = document.getElementById("problemCardLightbox");
      const problemCardLbBody = document.getElementById("problem-card-lb-body");
      const problemCardLbClose = document.getElementById("problem-card-lb-close");
      const problemCardLbBackdrop = problemCardLb?.querySelector(
        ".problem-card-lightbox-backdrop"
      );
      const mobileMq = window.matchMedia("(max-width: 900px)");
      const isMobileViewport = () => mobileMq.matches;

      const closeProblemCardLb = () => {
        if (!problemCardLb) return;
        problemCardLb.classList.remove("open");
        problemCardLb.setAttribute("aria-hidden", "true");
        document.body.classList.remove("problem-card-lightbox-open");
        if (problemCardLbBody) problemCardLbBody.innerHTML = "";
      };

      const openProblemCardLb = (card) => {
        if (!isMobileViewport() || !problemCardLb || !problemCardLbBody) return;
        const clone = card.cloneNode(true);
        clone.classList.add("card-visible", "problem-card-expanded");
        clone.setAttribute("aria-label", "Expanded challenge card");
        problemCardLbBody.innerHTML = "";
        problemCardLbBody.appendChild(clone);
        problemCardLb.classList.add("open");
        problemCardLb.setAttribute("aria-hidden", "false");
        document.body.classList.add("problem-card-lightbox-open");
      };

      if (problemCardLb) {
        const cardClickHandlers = [];
        problemCards.forEach((card) => {
          const onCardClick = () => openProblemCardLb(card);
          card.addEventListener("click", onCardClick);
          cardClickHandlers.push({ card, onCardClick });
        });

        const onBackdropClick = () => closeProblemCardLb();
        const onCloseClick = (event) => {
          event.stopPropagation();
          closeProblemCardLb();
        };
        const onProblemCardKeyDown = (event) => {
          if (event.key === "Escape" && problemCardLb.classList.contains("open")) {
            closeProblemCardLb();
          }
        };

        problemCardLbBackdrop?.addEventListener("click", onBackdropClick);
        problemCardLbClose?.addEventListener("click", onCloseClick);
        document.addEventListener("keydown", onProblemCardKeyDown);

        cleanups.push(() => {
          cardClickHandlers.forEach(({ card, onCardClick }) => {
            card.removeEventListener("click", onCardClick);
          });
          problemCardLbBackdrop?.removeEventListener("click", onBackdropClick);
          problemCardLbClose?.removeEventListener("click", onCloseClick);
          document.removeEventListener("keydown", onProblemCardKeyDown);
          closeProblemCardLb();
        });
      }
    }

    // Tech graph cards — sequential reveal after diagram enters view
    const techDiagramReveal = root.querySelector(".tech-diagram.reveal");
    const techGraphItems = root.querySelectorAll(".tech-graph-item");
    let techGraphTriggered = false;
    const techGraphRevealTimers = [];

    const revealTechGraphCards = () => {
      if (techGraphTriggered || !techGraphItems.length) return;
      techGraphTriggered = true;
      const staggerMs = 1000;
      const startDelayMs = 220;
      techGraphItems.forEach((item, index) => {
        const timerId = setTimeout(() => {
          item.classList.add("tech-graph-item-visible");
        }, startDelayMs + index * staggerMs);
        techGraphRevealTimers.push(timerId);
      });
    };

    if (techDiagramReveal && techGraphItems.length) {
      if (techDiagramReveal.classList.contains("visible")) {
        revealTechGraphCards();
      } else {
        const techGraphRevealObs = new MutationObserver(() => {
          if (techDiagramReveal.classList.contains("visible")) {
            revealTechGraphCards();
            techGraphRevealObs.disconnect();
          }
        });
        techGraphRevealObs.observe(techDiagramReveal, {
          attributes: true,
          attributeFilter: ["class"],
        });
        cleanups.push(() => techGraphRevealObs.disconnect());
      }
      cleanups.push(() => {
        techGraphRevealTimers.forEach((id) => clearTimeout(id));
      });
    }

    // Learning trajectory graph — nodes fade in one-by-one when graph enters view (~6.5s total)
    const ltGraphWrap = root.querySelector(".lt-graph-wrap");
    const ltGraphSvg = root.querySelector(".lt-graph-svg");
    const ltGraphActors = ltGraphSvg
      ? [...ltGraphSvg.querySelectorAll(".lt-graph-actor")].sort(
          (a, b) => Number(a.dataset.ltOrder || 0) - Number(b.dataset.ltOrder || 0)
        )
      : [];
    const ltGraphLines = ltGraphSvg
      ? [...ltGraphSvg.querySelectorAll(".lt-edges line")]
      : [];
    let ltGraphTriggered = false;
    const ltGraphRevealTimers = [];

    const resetLtGraph = () => {
      ltGraphSvg?.classList.remove("lt-edges-visible");

      ltGraphActors.forEach((actor) => {
        actor.classList.remove("lt-shown");
        actor.setAttribute("opacity", "0");
      });

      ltGraphLines.forEach((line) => {
        line.classList.remove("lt-edge-shown");
        line.setAttribute("opacity", "0");
      });
    };

    const getLtNodePosKey = (x, y) =>
      `${Math.round(Number(x))},${Math.round(Number(y))}`;

    const getLtActorPosKey = (actor) => {
      const transform = actor.getAttribute("transform");
      const match = transform?.match(/translate\(([-\d.]+)\s*,?\s*([-\d.]+)\)/);
      return match ? getLtNodePosKey(match[1], match[2]) : null;
    };

    const ltNodeOrderByPos = new Map(
      ltGraphActors
        .map((actor) => [getLtActorPosKey(actor), Number(actor.dataset.ltOrder || 0)])
        .filter(([key]) => key)
    );

    const getLtLineRevealOrder = (line) => {
      const p1 = getLtNodePosKey(line.getAttribute("x1"), line.getAttribute("y1"));
      const p2 = getLtNodePosKey(line.getAttribute("x2"), line.getAttribute("y2"));
      const o1 = ltNodeOrderByPos.get(p1) ?? 0;
      const o2 = ltNodeOrderByPos.get(p2) ?? 0;
      return Math.max(o1, o2);
    };

    const ltLinesByRevealOrder = ltGraphLines.reduce((groups, line) => {
      const order = getLtLineRevealOrder(line);
      if (!groups[order]) groups[order] = [];
      groups[order].push(line);
      return groups;
    }, {});

    const showLtLine = (line) => {
      line.classList.add("lt-edge-shown");
      line.setAttribute(
        "opacity",
        line.classList.contains("lt-edge-dashed") ? "0.78" : "1"
      );
    };

    const revealLtGraph = () => {
      if (ltGraphTriggered || !ltGraphSvg || !ltGraphActors.length) return;
      ltGraphTriggered = true;
      ltGraphSvg.classList.add("lt-edges-visible");

      const LT_GRAPH_DURATION_MS = 6500;
      const LT_NODE_FADE_MS = 420;
      const ltMaxOrder = ltGraphActors.reduce(
        (max, actor) => Math.max(max, Number(actor.dataset.ltOrder || 0)),
        0
      );
      const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const stepMs = reducedMotion
        ? 220
        : Math.round(
            (LT_GRAPH_DURATION_MS - LT_NODE_FADE_MS) / Math.max(ltMaxOrder, 1)
          );

      ltGraphSvg.style.setProperty("--lt-node-fade-ms", `${LT_NODE_FADE_MS}ms`);
      ltGraphSvg.style.setProperty("--lt-edge-fade-ms", `${Math.round(LT_NODE_FADE_MS * 0.75)}ms`);

      ltGraphActors.forEach((actor, index) => {
        const timerId = window.setTimeout(() => {
          actor.classList.add("lt-shown");
          actor.setAttribute("opacity", "1");

          const order = Number(actor.dataset.ltOrder || 0);
          (ltLinesByRevealOrder[order] || []).forEach(showLtLine);
        }, index * stepMs);
        ltGraphRevealTimers.push(timerId);
      });
    };

    const shouldRevealLtGraph = () => {
      if (ltGraphTriggered || !ltGraphWrap) return false;

      const rect = ltGraphWrap.getBoundingClientRect();
      const viewHeight = window.innerHeight || document.documentElement.clientHeight;

      return rect.top < viewHeight * 0.85 && rect.bottom > viewHeight * 0.15;
    };

    const tryRevealLtGraph = () => {
      if (shouldRevealLtGraph()) revealLtGraph();
    };

    if (ltGraphSvg && ltGraphActors.length && ltGraphWrap) {
      resetLtGraph();

      const ltGraphObs = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) tryRevealLtGraph();
          });
        },
        { threshold: 0.12, rootMargin: "0px 0px -5% 0px" }
      );
      ltGraphObs.observe(ltGraphWrap);

      const onLtScroll = () => tryRevealLtGraph();
      window.addEventListener("scroll", onLtScroll, { passive: true });
      window.addEventListener("resize", onLtScroll);

      requestAnimationFrame(() => tryRevealLtGraph());

      const ltGraphFallback = window.setTimeout(() => tryRevealLtGraph(), 5000);
      ltGraphRevealTimers.push(ltGraphFallback);

      cleanups.push(() => {
        ltGraphObs.disconnect();
        window.removeEventListener("scroll", onLtScroll);
        window.removeEventListener("resize", onLtScroll);
        ltGraphRevealTimers.forEach((id) => window.clearTimeout(id));
      });
    }

    // Copilot showcase — staggered feature reveal + CTA pulse
    let ctaAttentionTimer = null;
    let featureRevealTimers = [];
    const tabPlatforms = root.querySelector("#tab-platforms");
    const copilotShowcase = root.querySelector("#tab-platforms .copilot-showcase");
    const schoolCard = root.querySelector("#schoolCard");
    const studentCard = root.querySelector("#studentCard");
    const mobileCopilotMq = window.matchMedia("(max-width: 900px)");
    const isMobileCopilot = () => mobileCopilotMq.matches;

    const clearFeatureTimers = () => {
      featureRevealTimers.forEach((id) => clearTimeout(id));
      featureRevealTimers = [];
    };

    const resetCardFeatures = (card) => {
      card?.querySelectorAll(".copilot-showcase-feature").forEach((item) => {
        item.classList.remove("feature-revealed");
      });
    };

    const revealCardFeatures = (card) => {
      const items = card?.querySelectorAll(".copilot-showcase-feature");
      items?.forEach((item, index) => {
        const timerId = setTimeout(() => {
          item.classList.add("feature-revealed");
        }, index * 1000);
        featureRevealTimers.push(timerId);
      });
    };

    const closeCopilotFeatureDropdowns = () => {
      root.querySelectorAll("#tab-platforms .copilot-features-toggle").forEach((toggle) => {
        toggle.setAttribute("aria-expanded", "false");
      });
      root.querySelectorAll("#tab-platforms .copilot-showcase-features").forEach((panel) => {
        panel.classList.remove("is-open");
      });
    };

    const startFeatureReveal = () => {
      clearFeatureTimers();
      resetCardFeatures(schoolCard);
      resetCardFeatures(studentCard);
      closeCopilotFeatureDropdowns();
      if (!isMobileCopilot()) {
        revealCardFeatures(schoolCard);
        revealCardFeatures(studentCard);
      }
    };

    const resetCtaAttention = () => {
      root.querySelectorAll("#tab-platforms .copilot-showcase-cta").forEach((cta) => {
        cta.classList.remove("cta-attention");
      });
    };

    const triggerCtaAttention = () => {
      root.querySelectorAll("#tab-platforms .copilot-showcase-cta").forEach((cta) => {
        cta.classList.add("cta-attention");
      });
    };

    const clearCtaAttentionTimer = () => {
      if (ctaAttentionTimer) {
        clearTimeout(ctaAttentionTimer);
        ctaAttentionTimer = null;
      }
    };

    const scheduleCtaAttention = () => {
      clearCtaAttentionTimer();
      if (!tabPlatforms?.classList.contains("active") || isMobileCopilot()) return;
      ctaAttentionTimer = setTimeout(triggerCtaAttention, 3000);
    };

    const onShowcaseVisible = () => {
      startFeatureReveal();
      scheduleCtaAttention();
    };

    const onShowcaseHidden = () => {
      clearFeatureTimers();
      clearCtaAttentionTimer();
      resetCardFeatures(schoolCard);
      resetCardFeatures(studentCard);
      closeCopilotFeatureDropdowns();
      resetCtaAttention();
    };

    root.querySelectorAll("#tab-platforms .copilot-features-toggle").forEach((toggle) => {
      const panelId = toggle.getAttribute("aria-controls");
      const panel = panelId ? root.querySelector(`#${panelId}`) : null;
      const card = toggle.closest(".copilot-showcase-card");

      const onToggleClick = () => {
        if (!isMobileCopilot() || !panel) return;
        const isOpen = toggle.getAttribute("aria-expanded") === "true";
        if (isOpen) {
          toggle.setAttribute("aria-expanded", "false");
          panel.classList.remove("is-open");
          clearFeatureTimers();
          resetCardFeatures(card);
          return;
        }
        toggle.setAttribute("aria-expanded", "true");
        panel.classList.add("is-open");
        clearFeatureTimers();
        resetCardFeatures(card);
        revealCardFeatures(card);
      };

      toggle.addEventListener("click", onToggleClick);
      cleanups.push(() => toggle.removeEventListener("click", onToggleClick));
    });

    if (copilotShowcase) {
      const showcaseObs = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) onShowcaseVisible();
            else onShowcaseHidden();
          });
        },
        { threshold: 0.35 }
      );
      showcaseObs.observe(copilotShowcase);
      cleanups.push(() => {
        showcaseObs.disconnect();
        onShowcaseHidden();
      });
    }

    // Tab switching
    const switchTab = (tabName, btn) => {
      root.querySelectorAll(".tab-content").forEach((t) => t.classList.remove("active"));
      root.querySelectorAll(".tab-btn").forEach((b) => b.classList.remove("active"));
      root.querySelector("#tab-" + tabName)?.classList.add("active");
      btn?.classList.add("active");
      if (tabName === "platforms") onShowcaseVisible();
      else onShowcaseHidden();
    };
    root.querySelectorAll(".tab-btn").forEach((btn) => {
      const handler = (e) => {
        e.preventDefault();
        const tabName = btn.dataset.tab === "models" ? "models" : "platforms";
        switchTab(tabName, btn);
      };
      btn.addEventListener("click", handler);
      cleanups.push(() => btn.removeEventListener("click", handler));
    });

    // Smooth scroll for in-page anchors
    root.querySelectorAll('a[href^="#"]').forEach((a) => {
      const handler = (e) => {
        const id = a.getAttribute("href");
        if (id && id.length > 1) {
          e.preventDefault();
          const el = root.querySelector(id);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      };
      a.addEventListener("click", handler);
      cleanups.push(() => a.removeEventListener("click", handler));
    });

    const teamReadMoreToggle = root.querySelector(".team-read-more-toggle");
    const teamIntroPanel = root.querySelector("#teamIntroPanel");
    const mobileTeamMq = window.matchMedia("(max-width: 900px)");
    if (teamReadMoreToggle && teamIntroPanel) {
      const onTeamReadMoreClick = () => {
        if (!mobileTeamMq.matches) return;
        const isOpen = teamReadMoreToggle.getAttribute("aria-expanded") === "true";
        if (isOpen) {
          teamReadMoreToggle.setAttribute("aria-expanded", "false");
          teamIntroPanel.classList.remove("is-open");
          return;
        }
        teamReadMoreToggle.setAttribute("aria-expanded", "true");
        teamIntroPanel.classList.add("is-open");
      };
      teamReadMoreToggle.addEventListener("click", onTeamReadMoreClick);
      cleanups.push(() => teamReadMoreToggle.removeEventListener("click", onTeamReadMoreClick));
    }

    // Gallery lightbox (rendered outside root in NewHomepage.jsx)
    const lb = document.getElementById("galleryLightbox");
    if (lb) {
      const lbPhoto = document.getElementById("lb-photo");
      const lbLabel = document.getElementById("lb-label");
      const lbSub = document.getElementById("lb-sub");
      const lbClose = document.getElementById("lb-close-btn");

      const openLightbox = (item) => {
        const img = item.querySelector("img");
        if (!img || !lbPhoto) return;
        lbPhoto.src = img.currentSrc || img.src;
        lbPhoto.alt = img.alt || "Vidyam AI gallery";
        lbLabel.textContent = "Smiles & Stories";
        lbSub.textContent = "Vidyam AI — Real classrooms, real impact.";
        lb.classList.add("open");
      };

      const closeLightbox = () => lb.classList.remove("open");

      root.querySelectorAll(".gallery-item").forEach((item) => {
        item.style.cursor = "zoom-in";
        item.addEventListener("click", () => openLightbox(item));
        cleanups.push(() =>
          item.removeEventListener("click", () => openLightbox(item))
        );
      });

      const onLbClick = (e) => {
        if (e.target === lb) closeLightbox();
      };
      const onKeyDown = (e) => {
        if (e.key === "Escape") closeLightbox();
      };

      lb.addEventListener("click", onLbClick);
      lbClose?.addEventListener("click", closeLightbox);
      document.addEventListener("keydown", onKeyDown);
      cleanups.push(() => {
        lb.removeEventListener("click", onLbClick);
        lbClose?.removeEventListener("click", closeLightbox);
        document.removeEventListener("keydown", onKeyDown);
      });
    }

    // Intelligence videos lightbox
    const videoLb = document.getElementById("videoLightbox");
    if (videoLb) {
      const videoIframe = document.getElementById("video-lb-iframe");
      const videoClose = document.getElementById("video-lb-close-btn");

      const openVideoLightbox = (videoId) => {
        if (!videoId || !videoIframe) return;
        videoIframe.src = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
        videoLb.classList.add("open");
      };

      const closeVideoLightbox = () => {
        videoLb.classList.remove("open");
        if (videoIframe) videoIframe.src = "";
      };

      const intelligenceCarouselEl = root.querySelector(".intelligence-carousel");
      if (intelligenceCarouselEl) {
        const onIntelVideoClick = (e) => {
          const trigger = e.target.closest(".intern-card-video");
          if (!trigger || !intelligenceCarouselEl.contains(trigger)) return;
          const card = trigger.closest(".intern-card");
          if (!card?.dataset.videoId) return;
          openVideoLightbox(card.dataset.videoId);
        };
        intelligenceCarouselEl.addEventListener("click", onIntelVideoClick);
        cleanups.push(() => intelligenceCarouselEl.removeEventListener("click", onIntelVideoClick));
      }

      const teamInternCardsEl = root.querySelector(".team-intern-cards");
      if (teamInternCardsEl) {
        const onTeamInternVideoClick = (e) => {
          const trigger = e.target.closest(".intern-card-video");
          if (!trigger || !teamInternCardsEl.contains(trigger)) return;
          const card = trigger.closest(".intern-card");
          if (!card?.dataset.videoId) return;
          openVideoLightbox(card.dataset.videoId);
        };
        teamInternCardsEl.addEventListener("click", onTeamInternVideoClick);
        cleanups.push(() => teamInternCardsEl.removeEventListener("click", onTeamInternVideoClick));
      }

      const techCtas = root.querySelectorAll(".tech-cta[data-video-id]");
      techCtas.forEach((techCta) => {
        const onTechCtaClick = () => openVideoLightbox(techCta.dataset.videoId);
        techCta.addEventListener("click", onTechCtaClick);
        cleanups.push(() => techCta.removeEventListener("click", onTechCtaClick));
      });

      const copilotDemoBtns = root.querySelectorAll(".copilot-showcase-demo[data-video-id]");
      copilotDemoBtns.forEach((demoBtn) => {
        const onDemoClick = () => openVideoLightbox(demoBtn.dataset.videoId);
        demoBtn.addEventListener("click", onDemoClick);
        cleanups.push(() => demoBtn.removeEventListener("click", onDemoClick));
      });

      const trajectoryCtas = root.querySelectorAll(".lt-trajectory-cta[data-video-id]");
      trajectoryCtas.forEach((trajectoryCta) => {
        const onTrajectoryClick = () => openVideoLightbox(trajectoryCta.dataset.videoId);
        trajectoryCta.addEventListener("click", onTrajectoryClick);
        cleanups.push(() => trajectoryCta.removeEventListener("click", onTrajectoryClick));
      });

      const onVideoLbClick = (e) => {
        if (e.target === videoLb) closeVideoLightbox();
      };
      const onVideoKeyDown = (e) => {
        if (e.key === "Escape" && videoLb.classList.contains("open")) {
          closeVideoLightbox();
        }
      };

      videoLb.addEventListener("click", onVideoLbClick);
      videoClose?.addEventListener("click", closeVideoLightbox);
      document.addEventListener("keydown", onVideoKeyDown);
      cleanups.push(() => {
        videoLb.removeEventListener("click", onVideoLbClick);
        videoClose?.removeEventListener("click", closeVideoLightbox);
        document.removeEventListener("keydown", onVideoKeyDown);
        if (videoIframe) videoIframe.src = "";
      });

      // Intelligence in Action — infinite mobile video carousel
      const intelligenceCarousel = root.querySelector(".intelligence-carousel");
      if (intelligenceCarousel) {
        const viewport = intelligenceCarousel.querySelector(".intelligence-carousel-viewport");
        const track = intelligenceCarousel.querySelector(".intelligence-video-cards");
        const realCards = track ? [...track.querySelectorAll(".intern-card")] : [];
        realCards.forEach((card) => card.classList.add("visible"));
        const realCount = realCards.length;
        const prevBtn = intelligenceCarousel.querySelector(".intelligence-carousel-prev");
        const nextBtn = intelligenceCarousel.querySelector(".intelligence-carousel-next");
        const dotsRoot = intelligenceCarousel.querySelector(".intelligence-carousel-dots");
        const autoplayMs = Number(intelligenceCarousel.dataset.autoplayMs) || 3000;
        const mobileQuery = window.matchMedia("(max-width: 900px)");
        let visualIndex = 1;
        let autoplayTimer = null;
        let transitionFallbackTimer = null;
        let isAnimating = false;
        let dots = [];

        if (dotsRoot && realCount) {
          dotsRoot.replaceChildren();
          dots = realCards.map((_, index) => {
            const dot = document.createElement("button");
            dot.type = "button";
            dot.className = `intelligence-carousel-dot${index === 0 ? " is-active" : ""}`;
            dot.setAttribute("aria-label", `Go to video ${index + 1}`);
            dot.setAttribute("role", "tab");
            dot.setAttribute("aria-selected", index === 0 ? "true" : "false");
            dotsRoot.appendChild(dot);
            return dot;
          });
        }

        const isCarouselActive = () => mobileQuery.matches && realCount > 1;

        const getSlides = () => (track ? [...track.querySelectorAll(".intern-card")] : []);

        const getSlideWidth = () => {
          if (!viewport) return 0;
          return Math.round(viewport.getBoundingClientRect().width || viewport.offsetWidth);
        };

        const clearTransitionFallback = () => {
          if (transitionFallbackTimer) {
            clearTimeout(transitionFallbackTimer);
            transitionFallbackTimer = null;
          }
        };

        const finishCarouselTransition = () => {
          clearTransitionFallback();
          isAnimating = false;
          if (visualIndex === realCount + 1) {
            visualIndex = 1;
            setTrackPosition(false);
          } else if (visualIndex === 0) {
            visualIndex = realCount;
            setTrackPosition(false);
          } else if (visualIndex < 0 || visualIndex > realCount + 1) {
            visualIndex = Math.min(Math.max(visualIndex, 1), realCount);
            setTrackPosition(false);
          }
          updateDots();
        };

        const scheduleTransitionFallback = () => {
          clearTransitionFallback();
          transitionFallbackTimer = setTimeout(finishCarouselTransition, 780);
        };

        const getDotIndex = () => {
          if (visualIndex <= 0) return realCount - 1;
          if (visualIndex > realCount) return 0;
          return visualIndex - 1;
        };

        const teardownInfiniteClones = () => {
          track?.querySelectorAll(".intelligence-carousel-clone").forEach((node) => node.remove());
          visualIndex = 1;
        };

        const setupInfiniteClones = () => {
          teardownInfiniteClones();
          if (!isCarouselActive() || !track || realCount < 2) return;
          const cloneLast = realCards[realCount - 1].cloneNode(true);
          const cloneFirst = realCards[0].cloneNode(true);
          [cloneLast, cloneFirst].forEach((clone) => {
            clone.classList.add("intelligence-carousel-clone", "visible");
            clone.setAttribute("aria-hidden", "true");
            clone.querySelectorAll("[id]").forEach((el) => el.removeAttribute("id"));
          });
          track.insertBefore(cloneLast, realCards[0]);
          track.appendChild(cloneFirst);
          visualIndex = 1;
        };

        const applySlideLayout = () => {
          const slideWidth = getSlideWidth();
          const slides = getSlides();
          if (!slideWidth || !track || !slides.length) return slideWidth;
          slides.forEach((card) => {
            card.style.flexBasis = `${slideWidth}px`;
            card.style.width = `${slideWidth}px`;
            card.style.minWidth = `${slideWidth}px`;
          });
          track.style.width = `${slideWidth * slides.length}px`;
          return slideWidth;
        };

        const setTrackPosition = (animate = true) => {
          if (!isCarouselActive() || !viewport || !track) return false;
          const slideWidth = getSlideWidth();
          if (!slideWidth) return false;
          if (!animate) {
            track.classList.add("is-resetting");
            clearTransitionFallback();
            isAnimating = false;
          } else {
            isAnimating = true;
            scheduleTransitionFallback();
          }
          track.style.transform = `translate3d(-${slideWidth * visualIndex}px, 0, 0)`;
          if (!animate) {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                track.classList.remove("is-resetting");
              });
            });
          }
          return true;
        };

        const updateDots = () => {
          const dotIndex = getDotIndex();
          dots.forEach((dot, index) => {
            const active = index === dotIndex;
            dot.classList.toggle("is-active", active);
            dot.setAttribute("aria-selected", active ? "true" : "false");
          });
        };

        const onTrackTransitionEnd = (e) => {
          if (e.target !== track || e.propertyName !== "transform") return;
          finishCarouselTransition();
        };

        const goToDot = (dotIndex) => {
          if (!isCarouselActive() || isAnimating) return;
          const nextIndex = dotIndex + 1;
          if (nextIndex === visualIndex) return;
          visualIndex = nextIndex;
          setTrackPosition(true);
          updateDots();
        };

        const stepNext = () => {
          if (!isCarouselActive() || isAnimating) return;
          visualIndex += 1;
          setTrackPosition(true);
          updateDots();
        };

        const stepPrev = () => {
          if (!isCarouselActive() || isAnimating) return;
          visualIndex -= 1;
          setTrackPosition(true);
          updateDots();
        };

        const stopAutoplay = () => {
          if (autoplayTimer) {
            clearInterval(autoplayTimer);
            autoplayTimer = null;
          }
        };

        const startAutoplay = () => {
          stopAutoplay();
          if (!isCarouselActive()) return;
          autoplayTimer = setInterval(stepNext, autoplayMs);
        };

        const resetAutoplay = () => {
          stopAutoplay();
          startAutoplay();
        };

        const refreshCarousel = () => {
          if (isCarouselActive()) {
            setupInfiniteClones();
            const slideWidth = applySlideLayout();
            if (!slideWidth) return;
            isAnimating = false;
            clearTransitionFallback();
            setTrackPosition(false);
            updateDots();
            startAutoplay();
          } else {
            stopAutoplay();
            clearTransitionFallback();
            isAnimating = false;
            teardownInfiniteClones();
            if (track) {
              track.style.transform = "";
              track.style.width = "";
            }
            getSlides().forEach((card) => {
              card.style.flexBasis = "";
              card.style.width = "";
              card.style.minWidth = "";
            });
            updateDots();
          }
        };

        const onPrev = () => {
          stepPrev();
          resetAutoplay();
        };

        const onNext = () => {
          stepNext();
          resetAutoplay();
        };

        const onResize = () => {
          if (!isCarouselActive()) return;
          applySlideLayout();
          if (visualIndex < 1) visualIndex = realCount;
          else if (visualIndex > realCount) visualIndex = 1;
          isAnimating = false;
          clearTransitionFallback();
          setTrackPosition(false);
        };

        const onMobileChange = () => {
          refreshCarousel();
        };

        track?.addEventListener("transitionend", onTrackTransitionEnd);
        prevBtn?.addEventListener("click", onPrev);
        nextBtn?.addEventListener("click", onNext);
        dots.forEach((dot, index) => {
          const onDotClick = () => {
            goToDot(index);
            resetAutoplay();
          };
          dot.addEventListener("click", onDotClick);
          cleanups.push(() => dot.removeEventListener("click", onDotClick));
        });

        if (typeof mobileQuery.addEventListener === "function") {
          mobileQuery.addEventListener("change", onMobileChange);
          cleanups.push(() => mobileQuery.removeEventListener("change", onMobileChange));
        } else if (typeof mobileQuery.addListener === "function") {
          mobileQuery.addListener(onMobileChange);
          cleanups.push(() => mobileQuery.removeListener(onMobileChange));
        }

        window.addEventListener("resize", onResize);
        if (viewport) {
          const carouselObs = new ResizeObserver(() => {
            onResize();
            if (isCarouselActive() && !getSlideWidth()) refreshCarousel();
          });
          carouselObs.observe(viewport);
          cleanups.push(() => carouselObs.disconnect());
        }

        const carouselRevealObs = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) refreshCarousel();
            });
          },
          { threshold: 0.15 }
        );
        carouselRevealObs.observe(intelligenceCarousel);
        cleanups.push(() => carouselRevealObs.disconnect());

        requestAnimationFrame(() => {
          refreshCarousel();
        });

        cleanups.push(() => {
          track?.removeEventListener("transitionend", onTrackTransitionEnd);
          prevBtn?.removeEventListener("click", onPrev);
          nextBtn?.removeEventListener("click", onNext);
          window.removeEventListener("resize", onResize);
          stopAutoplay();
          clearTransitionFallback();
          teardownInfiniteClones();
          if (track) {
            track.style.transform = "";
            track.style.width = "";
          }
          realCards.forEach((card) => {
            card.style.flexBasis = "";
            card.style.width = "";
            card.style.minWidth = "";
          });
        });
      }
    }

    // Gallery marquee — match bottom row speed to top row
    const topGalleryTrack = root.querySelector("#galleryTrack");
    const bottomGalleryTrack = root.querySelector(".gallery-track-rev");
    if (topGalleryTrack && bottomGalleryTrack) {
      const baseDurationSec = 30;

      const syncGalleryMarqueeSpeed = () => {
        const topLoopWidth = topGalleryTrack.scrollWidth / 2;
        const bottomLoopWidth = bottomGalleryTrack.scrollWidth / 2;
        if (!topLoopWidth || !bottomLoopWidth) return;

        const pxPerSec = topLoopWidth / baseDurationSec;
        topGalleryTrack.style.animationDuration = `${baseDurationSec}s`;
        bottomGalleryTrack.style.animationDuration = `${bottomLoopWidth / pxPerSec}s`;
      };

      const runGallerySync = () => {
        requestAnimationFrame(syncGalleryMarqueeSpeed);
      };

      runGallerySync();
      window.addEventListener("resize", runGallerySync);
      window.addEventListener("load", runGallerySync);

      const galleryObs = new ResizeObserver(runGallerySync);
      galleryObs.observe(topGalleryTrack);
      galleryObs.observe(bottomGalleryTrack);

      topGalleryTrack.querySelectorAll("img").forEach((img) => {
        if (!img.complete) img.addEventListener("load", runGallerySync, { once: true });
      });
      bottomGalleryTrack.querySelectorAll("img").forEach((img) => {
        if (!img.complete) img.addEventListener("load", runGallerySync, { once: true });
      });

      cleanups.push(() => {
        window.removeEventListener("resize", runGallerySync);
        window.removeEventListener("load", runGallerySync);
        galleryObs.disconnect();
      });
    }

    // Technology diagram — sync SVG connectors to card positions
    const techDiagram = root.querySelector(".tech-diagram");
    if (techDiagram) {
      const SVG_NS = "http://www.w3.org/2000/svg";

      const placeFlowNodes = (link, x1, y1, x2, y2, count = 7, radii = { end: 3.8, mid: 3.2 }) => {
        const group = techDiagram.querySelector(`.tech-flow-node-group[data-link="${link}"]`);
        if (!group) return;
        group.replaceChildren();
        for (let i = 0; i <= count; i += 1) {
          const t = i / count;
          const circle = document.createElementNS(SVG_NS, "circle");
          circle.setAttribute("cx", String(x1 + (x2 - x1) * t));
          circle.setAttribute("cy", String(y1 + (y2 - y1) * t));
          circle.setAttribute("r", String(i === 0 || i === count ? radii.end : radii.mid));
          circle.setAttribute("class", "tech-flow-node");
          circle.style.animationDelay = `${i * 0.14}s`;
          group.appendChild(circle);
        }
      };

      const syncTechFlowSvg = () => {
        const svg = techDiagram.querySelector(".tech-flow-svg");
        if (!svg) return;

        const diagramRect = techDiagram.getBoundingClientRect();
        if (diagramRect.width < 1 || diagramRect.height < 1) return;

        const isMobileTech = window.matchMedia("(max-width: 900px)").matches;
        let vbW;
        let vbH;
        if (isMobileTech) {
          vbW = 1000;
          vbH = Math.max(640, Math.round((diagramRect.height / diagramRect.width) * vbW));
          svg.setAttribute("viewBox", `0 0 ${vbW} ${vbH}`);
          svg.setAttribute("preserveAspectRatio", "none");
        } else {
          vbW = 900;
          vbH = 420;
          svg.setAttribute("viewBox", "0 0 900 420");
          svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
        }

        const toSvg = (clientX, clientY) => ({
          x: ((clientX - diagramRect.left) / diagramRect.width) * vbW,
          y: ((clientY - diagramRect.top) / diagramRect.height) * vbH,
        });

        const rectPoint = (el, xFrac, yFrac) => {
          const r = el.getBoundingClientRect();
          return toSvg(r.left + r.width * xFrac, r.top + r.height * yFrac);
        };

        const schoolCopilot = techDiagram.querySelector(".tech-copilot-school");
        const studentCopilot = techDiagram.querySelector(".tech-copilot-student");
        const brainHub = techDiagram.querySelector(".tech-brain-hub");
        const pedagogyPanel = techDiagram.querySelector(".tech-pedagogy-panel");
        if (!schoolCopilot || !studentCopilot || !brainHub || !pedagogyPanel) return;

        if (isMobileTech) {
          const vGap = (8 / diagramRect.height) * vbH;
          const dotRadii = { end: 4.6, mid: 3.8 };
          ["school", "student"].forEach((link) => {
            const group = techDiagram.querySelector(`.tech-flow-node-group[data-link="${link}"]`);
            group?.replaceChildren();
          });
          const hubBottom = rectPoint(brainHub, 0.5, 1);
          const panelTop = rectPoint(pedagogyPanel, 0.5, 0);

          placeFlowNodes(
            "pedagogy",
            hubBottom.x,
            hubBottom.y + vGap,
            panelTop.x,
            panelTop.y - vGap,
            9,
            dotRadii
          );
          return;
        }

        const hGap = (10 / diagramRect.width) * vbW;
        const schoolRight = rectPoint(schoolCopilot, 1, 0.5);
        const studentRight = rectPoint(studentCopilot, 1, 0.5);
        const hubLeft = rectPoint(brainHub, 0, 0.5);
        const hubRight = rectPoint(brainHub, 1, 0.5);
        const panelLeft = rectPoint(pedagogyPanel, 0, 0.5);

        placeFlowNodes(
          "school",
          schoolRight.x + hGap,
          schoolRight.y,
          hubLeft.x - hGap,
          schoolRight.y
        );
        placeFlowNodes(
          "student",
          studentRight.x + hGap,
          studentRight.y,
          hubLeft.x - hGap,
          studentRight.y
        );
        placeFlowNodes(
          "pedagogy",
          hubRight.x + hGap,
          hubRight.y,
          panelLeft.x - hGap,
          hubRight.y
        );
      };

      const runSync = () => {
        requestAnimationFrame(() => {
          syncTechFlowSvg();
          requestAnimationFrame(syncTechFlowSvg);
        });
      };
      runSync();
      window.addEventListener("resize", runSync);
      window.addEventListener("load", runSync);
      const techObs = new ResizeObserver(runSync);
      techObs.observe(techDiagram);
      techDiagram.querySelectorAll(".tech-copilot, .tech-brain-hub, .tech-pedagogy-panel").forEach((el) => {
        techObs.observe(el);
      });
      const techRevealObs = new MutationObserver(runSync);
      techRevealObs.observe(techDiagram, { attributes: true, subtree: true, attributeFilter: ["class"] });
      cleanups.push(() => {
        window.removeEventListener("resize", runSync);
        window.removeEventListener("load", runSync);
        techObs.disconnect();
        techRevealObs.disconnect();
      });
    }

    return () => cleanups.forEach((fn) => fn());
  }, [rootRef, html]);
}
